import { useState, FormEvent } from 'react';
import emailjs from '@emailjs/browser';

const EMAILJS_SERVICE_ID = 'service_h2lg0qu';
const EMAILJS_TEMPLATE_ID = 'template_hzykyfp';
const EMAILJS_PUBLIC_KEY = 'b1rgYfFEeLDlqMWl5';

const EnquiryForm = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    budget: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Send via EmailJS
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          fullName: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          company: formData.company,
          service: formData.service,
          message: formData.message,
        },
        EMAILJS_PUBLIC_KEY
      );

      // Also submit to Readdy form
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });
      await fetch('https://readdy.ai/api/form/d4raq0gnlksjl5n042g0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      });

      setSubmitStatus('success');
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        company: '',
        service: '',
        budget: '',
        message: '',
      });
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
          {/* Left Contact Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Email Card */}
            <a
              href="mailto:growthlink2705@gmail.com"
              className="block bg-white/50 backdrop-blur-md rounded-2xl p-8 border border-gray-200 hover:border-primary hover:shadow-xl transition-all duration-300 hover:scale-105 group"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <i className="ri-mail-line text-3xl text-white"></i>
              </div>
              <div className="text-lg font-semibold text-gray-700 mb-2">Email Us</div>
              <div className="text-2xl font-bold text-dark">growthlink2705@gmail.com</div>
            </a>

            {/* WhatsApp Card */}
            <a
              href="https://wa.me/918931893177"
              target="_blank"
              rel="noopener noreferrer"
              className="block bg-white/50 backdrop-blur-md rounded-2xl p-8 border border-gray-200 hover:border-[#25D366] hover:shadow-xl transition-all duration-300 hover:scale-105 group"
            >
              <div className="w-16 h-16 bg-[#25D366] rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <i className="ri-whatsapp-line text-3xl text-white"></i>
              </div>
              <div className="text-lg font-semibold text-gray-700 mb-2">WhatsApp</div>
              <div className="text-2xl font-bold text-dark">+91 8931893177</div>
            </a>

            {/* Location Card */}
            <div className="bg-white/50 backdrop-blur-md rounded-2xl p-8 border border-gray-200">
              <div className="w-16 h-16 bg-gradient-to-br from-accent to-[#F59E0B] rounded-xl flex items-center justify-center mb-4">
                <i className="ri-map-pin-line text-3xl text-white"></i>
              </div>
              <div className="text-lg font-semibold text-gray-700 mb-2">Visit Us</div>
              <div className="text-base text-gray-600">
                Growthlink Marketing Solutions<br />
                Serlingampally<br />
                Hyderabad 500082
              </div>
            </div>

            {/* Social Links */}
            <div className="flex gap-4 pt-4">
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-primary hover:text-white transition-colors">
                <i className="ri-linkedin-fill text-xl"></i>
              </a>
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-primary hover:text-white transition-colors">
                <i className="ri-twitter-x-line text-xl"></i>
              </a>
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-primary hover:text-white transition-colors">
                <i className="ri-instagram-line text-xl"></i>
              </a>
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-primary hover:text-white transition-colors">
                <i className="ri-facebook-fill text-xl"></i>
              </a>
            </div>
          </div>

          {/* Right Form */}
          <div className="lg:col-span-3">
            <form
              id="enquiry_form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-white rounded-3xl p-12 shadow-xl border border-gray-100"
            >
              <h2 className="text-3xl font-bold text-dark mb-8">Send us an Enquiry</h2>

              <div className="space-y-6">
                {/* Full Name */}
                <div className="relative">
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    required
                    className="w-full h-14 px-4 pt-6 pb-2 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none transition-colors peer text-base"
                    placeholder=" "
                  />
                  <label className="absolute left-4 top-4 text-gray-500 text-base transition-all peer-focus:top-2 peer-focus:text-xs peer-focus:text-primary peer-[:not(:placeholder-shown)]:top-2 peer-[:not(:placeholder-shown)]:text-xs">
                    Full Name *
                  </label>
                </div>

                {/* Email */}
                <div className="relative">
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full h-14 px-4 pt-6 pb-2 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none transition-colors peer text-base"
                    placeholder=" "
                  />
                  <label className="absolute left-4 top-4 text-gray-500 text-base transition-all peer-focus:top-2 peer-focus:text-xs peer-focus:text-primary peer-[:not(:placeholder-shown)]:top-2 peer-[:not(:placeholder-shown)]:text-xs">
                    Email Address *
                  </label>
                </div>

                {/* Phone */}
                <div className="relative">
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full h-14 px-4 pt-6 pb-2 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none transition-colors peer text-base"
                    placeholder=" "
                  />
                  <label className="absolute left-4 top-4 text-gray-500 text-base transition-all peer-focus:top-2 peer-focus:text-xs peer-focus:text-primary peer-[:not(:placeholder-shown)]:top-2 peer-[:not(:placeholder-shown)]:text-xs">
                    Phone Number *
                  </label>
                </div>

                {/* Company */}
                <div className="relative">
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full h-14 px-4 pt-6 pb-2 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none transition-colors peer text-base"
                    placeholder=" "
                  />
                  <label className="absolute left-4 top-4 text-gray-500 text-base transition-all peer-focus:top-2 peer-focus:text-xs peer-focus:text-primary peer-[:not(:placeholder-shown)]:top-2 peer-[:not(:placeholder-shown)]:text-xs">
                    Company Name
                  </label>
                </div>

                {/* Service Interest */}
                <div className="relative">
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    required
                    className="w-full h-14 px-4 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none transition-colors text-base appearance-none cursor-pointer"
                  >
                    <option value="">Select Service Interest *</option>
                    <option value="SEO Optimization">SEO Optimization</option>
                    <option value="Social Media Marketing">Social Media Marketing</option>
                    <option value="Content Strategy">Content Strategy</option>
                    <option value="PPC Advertising">PPC Advertising</option>
                    <option value="Email Marketing">Email Marketing</option>
                    <option value="Brand Development">Brand Development</option>
                    <option value="Full Service Package">Full Service Package</option>
                  </select>
                  <i className="ri-arrow-down-s-line absolute right-4 top-4 text-xl text-gray-500 pointer-events-none"></i>
                </div>

                {/* Budget Range */}
                {/* Removed budget field */}

                {/* Message */}
                <div className="relative">
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    maxLength={500}
                    required
                    rows={5}
                    className="w-full px-4 pt-6 pb-2 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none transition-colors peer text-base resize-none"
                    placeholder=" "
                  ></textarea>
                  <label className="absolute left-4 top-4 text-gray-500 text-base transition-all peer-focus:top-2 peer-focus:text-xs peer-focus:text-primary peer-[:not(:placeholder-shown)]:top-2 peer-[:not(:placeholder-shown)]:text-xs">
                    Message * (Max 500 characters)
                  </label>
                  <div className="absolute bottom-3 right-4 text-xs text-gray-400">
                    {formData.message.length}/500
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full h-16 bg-dark text-white font-semibold rounded-xl hover:bg-primary transition-all duration-300 flex items-center justify-center gap-3 text-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-2xl"></i>
                      Sending...
                    </>
                  ) : submitStatus === 'success' ? (
                    <>
                      <i className="ri-checkbox-circle-line text-2xl"></i>
                      Sent Successfully!
                    </>
                  ) : (
                    <>
                      Send Enquiry
                      <i className="ri-send-plane-fill text-xl"></i>
                    </>
                  )}
                </button>

                {submitStatus === 'error' && (
                  <p className="text-red-500 text-sm text-center">
                    Something went wrong. Please try again.
                  </p>
                )}

                {/* Privacy Note */}
                <p className="text-xs text-gray-500 text-center leading-relaxed">
                  By submitting this form, you agree to our privacy policy. We respect your data and will never share it with third parties.
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EnquiryForm;
